// frontend/src/pages/motivos-mba/MotivoMbaFormPage.jsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';

function MotivoMbaFormPage() {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);        // loading de tela/submit
  const [initialLoading, setInitialLoading] = useState(false); // loading do fetch inicial (edição)
  const [error, setError] = useState(null);             // pode ser { kind: 'permission' } ou erro genérico
  const abortRef = useRef(null);

  const navigate = useNavigate();
  const { id } = useParams();

  const pageTitle = id ? 'Editar Motivo MBA' : 'Novo Motivo MBA';
  const pageSubtitle = id ? `Editando o registro #${id}` : 'Preencha os dados para criar um novo motivo MBA';

  // Carrega dados para edição
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const load = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const resp = await api.get(`/motivos-mba/${id}`, { signal: controller.signal });
        const payload = resp?.data?.data || resp?.data || {};
        setMotivo(payload?.motivo || '');
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setInitialLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [id]);

  // Submit (criar/atualizar)
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setError(null);

    let ordem = 1;
    if (!id) {
      // Best-effort para definir ordem (se falhar, mantém 1)
      try {
        const response = await api.get('/motivos-mba', {
          params: {
            per_page: 10000,
            status: 'active',
          },
        });
        const lista = response?.data?.data || response?.data || [];
        const maxOrdem = Array.isArray(lista)
          ? lista.reduce((max, item) => (Number(item?.ordem) > max ? Number(item?.ordem) : max), 0)
          : 0;
        ordem = maxOrdem + 1;
      } catch {
        // silencioso; backend pode validar/ajustar
      }
    }

    const payload = { motivo };
    if (!id) payload.ordem = ordem;

    try {
      if (id) {
        await api.put(`/motivos-mba/${id}`, payload);
      } else {
        await api.post('/motivos-mba', payload);
      }
      navigate('/painel/motivos-mba');
    } catch (err) {
      if (err?.response?.status === 403) {
        setError({ kind: 'permission' });
      } else if (err?.response?.data?.message) {
        // Ignora especificamente mensagens relacionadas à 'ordem' se o backend exigir
        const msg = String(err.response.data.message || '');
        setError(msg.toLowerCase().includes('ordem') ? null : err);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // GATES
  if (initialLoading) {
    return (
      <div className="motivos-mba-form-page">
        <LoadingSpinner text="Carregando dados do motivo MBA..." />
      </div>
    );
  }
  if (error?.kind === 'permission') {
    return (
      <div className="motivos-mba-form-page">
        <PermissionDeniedAlert action={id ? 'editar motivo MBA' : 'criar motivo MBA'} />
      </div>
    );
  }

  return (
    <div className="motivos-mba-form-page">
      <div className="content-header">
        <h1 className="content-title">{pageTitle}</h1>
        <p className="content-subtitle">{pageSubtitle}</p>
      </div>

      <div className="card motivos-mba-form-card">
        <div className="card-body">
          {/* Erros não-permission exibidos de forma elegante */}
          {error && error.kind !== 'permission' && (
            <AlertException error={error} fallback="Ocorreu um erro ao salvar/carregar o motivo MBA." />
          )}

          <form className="motivos-mba-form" onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="motivo" className="form-label">Motivo MBA</label>
              <input
                type="text"
                className="form-control"
                id="motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                required
                maxLength={150}
              />
            </div>

            <div className="motivos-mba-form-actions d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => navigate('/painel/motivos-mba')}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MotivoMbaFormPage;
